import { Card, Grid, Icon, InputLabel, Menu, MenuItem, Select } from "@mui/material";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import React, { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import DataTable from "components/Tables/DataTable";
import dataVehiculo from "./dataVehiculo";
import SoftBox from "components/SoftBox";
import { getColor } from "utils/colors";
import SinDatos from "components/Carteles/SinDatos";
import Separador from "../Separador";
import ModalAnteriores from "../ModalAnteriores";

const Vehiculos = ({
  idDeclaracion,
  listadoVehiculos,
  setListadoVehiculos,
  finalizada,
  idEmpresa,
  mostrarAnteriores = true,
}) => {
  const [vehiculo, setVehiculo] = useState({});
  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [modalAnteriores, setModalAnteriores] = useState(false);

  const { columns, getRows } = dataVehiculo;

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedVehiculo(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
    setSelectedVehiculo(null);
  }, []);

  const handleChangeVehiculo = (e) => {
    const { name, value } = e.target;
    setVehiculo((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddVehiculo = () => {
    const newVehiculo = { ...vehiculo };
    setListadoVehiculos((prev) => [...prev, newVehiculo]);
    setVehiculo({});
  };

  const memoizedRows = useMemo(() => {
    if (listadoVehiculos) {
      const listadoSinBorrar = listadoVehiculos.filter((item) => !item.aBorrar);
      return getRows(listadoSinBorrar, handleOpenMenu);
    }
    return [];
  }, [listadoVehiculos, handleOpenMenu]);

  // Render del menú contextual
  const renderMenu = !finalizada && (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem
        onClick={() => {
          handleCloseMenu();
          setListadoVehiculos((prev) => {
            if (selectedVehiculo.id) {
              return prev.map((item) => {
                if (item.id === selectedVehiculo.id) {
                  return { ...item, aBorrar: true };
                }
                return item;
              });
            } else {
              return prev.filter((item) => item !== selectedVehiculo);
            }
          });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Quitar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            delete
          </Icon>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  return (
    <Grid container p={2}>
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="flex-end">
        <SoftTypography variant="h4" fontWeight="bold">
          Vehículos
        </SoftTypography>
        {!finalizada && mostrarAnteriores && (
          <SoftButton
            variant="outlined"
            color="dark"
            sx={{
              cursor: "pointer",
              p: 1,
            }}
            onClick={() => setModalAnteriores(true)}
            disabled={finalizada}
          >
            CARGAR ANTERIORES
            <Icon fontSize="small" sx={{ verticalAlign: "middle", color: "gobSecondary", ml: 0.5 }}>
              cached
            </Icon>
          </SoftButton>
        )}
      </Grid>
      {!finalizada && (
        <Grid item xs={12}>
          <Grid container p={2} spacing={1}>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="tipo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Tipo
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="tipo"
                placeholder="Tipo"
                value={vehiculo.tipo || ""}
                fullWidth
                name="tipo"
                onChange={handleChangeVehiculo}
              />
            </Grid>
            <Grid item xs={6} md={6}>
              <InputLabel htmlFor="marcaModelo">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Marca/Modelo
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="marcaModelo"
                placeholder="Marca/Modelo"
                value={vehiculo.marcaModelo || ""}
                fullWidth
                name="marcaModelo"
                onChange={handleChangeVehiculo}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <InputLabel htmlFor="dominio">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Dominio
                </SoftTypography>
              </InputLabel>
              <SoftInput
                id="dominio"
                placeholder="Dominio"
                value={vehiculo.dominio || ""}
                fullWidth
                name="dominio"
                onChange={handleChangeVehiculo}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <InputLabel htmlFor="tallerPropio">
                <SoftTypography component="label" variant="caption" fontWeight="bold">
                  Taller Propio
                </SoftTypography>
              </InputLabel>
              <Select
                id="tallerPropio"
                value={vehiculo.tallerPropio ?? ""}
                fullWidth
                name="tallerPropio"
                onChange={handleChangeVehiculo}
                defaultValue=""
                displayEmpty
                sx={{
                  fontStyle: vehiculo.tallerPropio === undefined ? "italic" : "normal",
                }}
              >
                <MenuItem value="" disabled>
                  Seleccione
                </MenuItem>
                <MenuItem value={true}>Sí</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12} md={4} display={"flex"} alignItems="flex-end">
              <SoftButton
                variant="gradient"
                color="primary"
                fullWidth
                onClick={handleAddVehiculo}
                disabled={!vehiculo.tipo || !vehiculo.marcaModelo || !vehiculo.dominio}
              >
                Agregar +
              </SoftButton>
            </Grid>
          </Grid>
        </Grid>
      )}
      {!finalizada && <Separador />}
      <Grid item xs={12} py={1}>
        <Card>
          {listadoVehiculos?.filter((item) => !item.aBorrar).length > 0 ? (
            <DataTable columns={columns} rows={memoizedRows} />
          ) : (
            <SinDatos textoTitulo="No hay vehículos cargados" />
          )}
        </Card>
        {renderMenu}
      </Grid>
      <ModalAnteriores
        open={modalAnteriores}
        handleClose={() => setModalAnteriores(false)}
        idEmpresa={idEmpresa}
        setListado={setListadoVehiculos}
        tipo="vehiculos"
      />
    </Grid>
  );
};

export default memo(Vehiculos);

Vehiculos.propTypes = {
  idDeclaracion: PropTypes.string,
  listadoVehiculos: PropTypes.array.isRequired,
  setListadoVehiculos: PropTypes.func.isRequired,
  finalizada: PropTypes.bool,
  idEmpresa: PropTypes.string,
  mostrarAnteriores: PropTypes.bool,
};
